import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, Mic,
  Download, Loader2, Clock, Check, RefreshCw, ChevronDown, ChevronRight,
  Filter, X, Grid3x3,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format, subDays } from "date-fns";
import { Dialpad } from "./dialpad";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Call {
  id: string;
  direction: string;
  status: string;
  fromNumber: string | null;
  toNumber: string | null;
  durationSeconds: number | null;
  notes: string | null;
  recordingUrl: string | null;
  readAt: string | null;
  createdAt: string;
  leadId?: string | null;
  clientId?: string | null;
}

interface CallsResp {
  items: Call[];
  nextCursor: string | null;
}

interface Voicemail {
  id: string;
  fromNumber: string | null;
  recordingUrl: string | null;
  durationSeconds: number;
  transcription: string | null;
  listenedAt: string | null;
  createdAt: string;
}

interface MyLineResp {
  provisioned: boolean;
  phoneNumber: string | null;
  type: "workspace" | "personal" | null;
  cnam: string | null;
  status: string | null;
}

interface ResolvedCaller {
  name: string;
  type: "lead" | "client" | "contact";
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDuration(secs: number | null | undefined): string {
  if (!secs) return "0s";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatPhone(p: string | null | undefined): string {
  if (!p) return "Unknown";
  const d = p.replace(/\D/g, "");
  if (d.length === 11 && d[0] === "1") {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
}

function DirectionIcon({ direction, status }: { direction: string; status: string }) {
  if (direction === "inbound" && status === "no-answer") {
    return <PhoneMissed className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />;
  }
  if (direction === "inbound") {
    return <PhoneIncoming className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />;
  }
  return <PhoneOutgoing className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />;
}

function DispositionBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; classes: string }> = {
    completed: { label: "Completed", classes: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    "no-answer": { label: "No answer", classes: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
    busy: { label: "Busy", classes: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" },
    failed: { label: "Failed", classes: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" },
    queued: { label: "Queued", classes: "bg-muted text-muted-foreground" },
    "in-progress": { label: "In progress", classes: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
  };
  const entry = map[status] || { label: status, classes: "bg-muted text-muted-foreground" };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${entry.classes}`}>
      {entry.label}
    </span>
  );
}

// ── Call Row ──────────────────────────────────────────────────────────────────
function CallRow({ call, onMarkRead }: { call: Call; onMarkRead: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [crmName, setCrmName] = useState<string | null>(null);
  const isMissed = call.direction === "inbound" && call.status === "no-answer";
  const isUnread = isMissed && !call.readAt;
  const phone = call.direction === "inbound" ? call.fromNumber : call.toNumber;

  const handleExpand = useCallback(async () => {
    setExpanded((v) => !v);
    if (!expanded && phone && !crmName) {
      try {
        const res = await fetch(`/api/dialer/resolve-caller?phone=${encodeURIComponent(phone)}`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.name) setCrmName(data.name);
        }
      } catch {/* ignore */}
    }
    if (isUnread) onMarkRead(call.id);
  }, [expanded, phone, crmName, isUnread, call.id, onMarkRead]);

  return (
    <div
      className={`rounded-lg border border-border/60 p-3 transition-colors cursor-pointer ${isUnread ? "bg-red-50/40 dark:bg-red-950/20" : "hover:bg-accent/30"}`}
      onClick={handleExpand}
      data-testid={`row-call-${call.id}`}
    >
      <div className="flex items-center gap-2.5">
        <DirectionIcon direction={call.direction} status={call.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate" data-testid={`text-call-phone-${call.id}`}>
              {crmName ? `${crmName} · ` : ""}{formatPhone(phone)}
            </span>
            {isUnread && <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" data-testid={`badge-unread-${call.id}`} />}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
            <Clock className="h-3 w-3" />
            {call.createdAt ? formatDistanceToNow(new Date(call.createdAt), { addSuffix: true }) : "—"}
            {(call.durationSeconds ?? 0) > 0 && <><span>·</span><span>{formatDuration(call.durationSeconds)}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <DispositionBadge status={call.status} />
          {call.recordingUrl && <Mic className="h-3 w-3 text-muted-foreground" />}
          {expanded ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/40 space-y-2" onClick={(e) => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div><span className="font-medium">Direction:</span> {call.direction}</div>
            <div><span className="font-medium">Status:</span> {call.status}</div>
            {call.durationSeconds !== null && (
              <div><span className="font-medium">Duration:</span> {formatDuration(call.durationSeconds)}</div>
            )}
            {call.createdAt && (
              <div><span className="font-medium">Time:</span> {format(new Date(call.createdAt), "MMM d h:mm a")}</div>
            )}
          </div>
          {call.notes && (
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">{call.notes}</div>
          )}
          {call.recordingUrl && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium text-muted-foreground">Recording</div>
              <audio controls src={`/api/dialer/calls/${call.id}/recording`} className="w-full h-8" data-testid={`audio-call-${call.id}`} />
              <a
                href={`/api/dialer/calls/${call.id}/recording`}
                download
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid={`link-recording-download-${call.id}`}
              >
                <Download className="h-3 w-3" /> Download
              </a>
            </div>
          )}
          {isMissed && phone && (
            <a
              href={`tel:${phone}`}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              data-testid={`link-callback-${call.id}`}
            >
              <Phone className="h-3 w-3" /> Call back
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity Tab ──────────────────────────────────────────────────────────────
const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

function ActivityTab({ onOpen }: { onOpen: boolean }) {
  const { toast } = useToast();
  const [direction, setDirection] = useState("all");
  const [disposition, setDisposition] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [recorded, setRecorded] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, refetch } = useQuery<CallsResp>({
    queryKey: ["/api/dialer/calls", { direction, disposition, dateRange, recorded }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (direction !== "all") params.set("direction", direction);
      if (disposition !== "all") params.set("status", disposition);
      if (dateRange !== "all") {
        const days = parseInt(dateRange, 10);
        if (!isNaN(days)) {
          params.set("since", subDays(new Date(), days).toISOString());
        }
      }
      const res = await fetch(`/api/dialer/calls?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load calls");
      return res.json();
    },
    staleTime: 30_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/dialer/calls/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/calls"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/unread-counts"] });
    },
  });

  // Bulk mark-read: when the hub opens, mark all visible missed unread calls as read.
  const markAllRead = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.allSettled(ids.map((id) => apiRequest("PATCH", `/api/dialer/calls/${id}/read`, {})));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/calls"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/unread-counts"] });
    },
  });

  // On hub open, bulk-mark all unread missed calls as read.
  // Depends on both `onOpen` and `data` so it fires once data arrives
  // while the hub is already open (race: data loads after hub renders).
  useEffect(() => {
    if (!onOpen || !data?.items) return;
    const unreadIds = data.items
      .filter((c) => c.direction === "inbound" && c.status === "no-answer" && !c.readAt)
      .map((c) => c.id);
    if (unreadIds.length > 0) {
      markAllRead.mutate(unreadIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOpen, data?.items]);

  // Filter items for recorded/not-recorded client-side.
  const filteredCalls = (data?.items ?? []).filter((c) => {
    if (recorded === "recorded") return !!c.recordingUrl;
    if (recorded === "not_recorded") return !c.recordingUrl;
    return true;
  });

  const hasFilters = direction !== "all" || disposition !== "all" || dateRange !== "all" || recorded !== "all";

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter controls */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 text-xs ${hasFilters ? "border-primary text-primary" : ""}`}
          onClick={() => setShowFilters((v) => !v)}
          data-testid="button-toggle-filters"
        >
          <Filter className="h-3 w-3" />
          Filters
          {hasFilters && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary" />}
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => refetch()} data-testid="button-refresh-calls">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground gap-1"
            onClick={() => { setDirection("all"); setDisposition("all"); setDateRange("all"); setRecorded("all"); }}
            data-testid="button-clear-filters"
          >
            <X className="h-3 w-3" /> Clear
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 gap-2">
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-filter-direction">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All directions</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={disposition} onValueChange={setDisposition}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-filter-disposition">
              <SelectValue placeholder="Disposition" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All dispositions</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="no-answer">No answer</SelectItem>
              <SelectItem value="busy">Busy</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-filter-date-range">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={recorded} onValueChange={setRecorded}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-filter-recorded">
              <SelectValue placeholder="Recording" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All calls</SelectItem>
              <SelectItem value="recorded">Recorded only</SelectItem>
              <SelectItem value="not_recorded">Not recorded</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {filteredCalls.length === 0 && (
        <div className="py-10 text-center text-sm text-muted-foreground">No calls match the selected filters.</div>
      )}

      {/* Scroll list — rows expand to variable height; a virtualizer with
          fixed estimateSize clips expanded content, so we use a standard
          overflow scroll here where each row self-reports its own height. */}
      <ScrollArea style={{ height: "calc(100vh - 380px)" }}>
        <div className="space-y-1 pr-1">
          {filteredCalls.map((call) => (
            <CallRow
              key={call.id}
              call={call}
              onMarkRead={(id) => markRead.mutate(id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

// ── Voicemails Tab ────────────────────────────────────────────────────────────
function VoicemailsTab() {
  const { toast } = useToast();

  const { data: voicemails = [], isLoading, refetch } = useQuery<Voicemail[]>({
    queryKey: ["/api/dialer/voicemails"],
    staleTime: 30_000,
  });

  const markListened = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/dialer/voicemails/${id}/listened`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/voicemails"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dialer/unread-counts"] });
    },
    onError: () => toast({ title: "Failed to update voicemail", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (voicemails.length === 0) {
    return <div className="py-10 text-center text-sm text-muted-foreground">No voicemails.</div>;
  }

  return (
    <div className="space-y-2">
      {voicemails.map((vm) => {
        const isNew = !vm.listenedAt;
        return (
          <div
            key={vm.id}
            className={`rounded-lg border border-border/60 p-3 space-y-2 ${isNew ? "bg-blue-50/40 dark:bg-blue-950/20" : ""}`}
            data-testid={`row-voicemail-${vm.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Voicemail className="h-4 w-4 text-primary flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    {formatPhone(vm.fromNumber)}
                    {isNew && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" data-testid={`badge-vm-new-${vm.id}`} />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {vm.createdAt ? formatDistanceToNow(new Date(vm.createdAt), { addSuffix: true }) : "—"}
                    {vm.durationSeconds > 0 && ` · ${formatDuration(vm.durationSeconds)}`}
                  </div>
                </div>
              </div>
              {isNew && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs gap-1"
                  onClick={() => markListened.mutate(vm.id)}
                  disabled={markListened.isPending}
                  data-testid={`button-vm-handled-${vm.id}`}
                >
                  <Check className="h-3 w-3" /> Mark handled
                </Button>
              )}
            </div>

            {vm.recordingUrl && (
              <audio
                controls
                src={`/api/dialer/voicemails/${vm.id}/recording`}
                className="w-full h-8"
                onPlay={() => { if (isNew) markListened.mutate(vm.id); }}
                data-testid={`audio-voicemail-${vm.id}`}
              />
            )}

            {vm.transcription && (
              <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 italic" data-testid={`text-vm-transcription-${vm.id}`}>
                "{vm.transcription}"
              </div>
            )}

            {vm.fromNumber && (
              <a
                href={`tel:${vm.fromNumber}`}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid={`link-vm-callback-${vm.id}`}
              >
                <Phone className="h-3 w-3" /> Call back
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Recordings Tab ────────────────────────────────────────────────────────────
const RECORDING_RETENTION_DAYS = 90;

function retentionLabel(createdAt: string): { label: string; isExpiringSoon: boolean } {
  const created = new Date(createdAt);
  const expiresAt = new Date(created.getTime() + RECORDING_RETENTION_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
  if (daysLeft <= 0) return { label: "Expired", isExpiringSoon: true };
  if (daysLeft <= 7) return { label: `Expires in ${daysLeft}d`, isExpiringSoon: true };
  return { label: `Kept ${RECORDING_RETENTION_DAYS - daysLeft}/${RECORDING_RETENTION_DAYS}d`, isExpiringSoon: false };
}

function RecordingsTab() {
  const { data, isLoading } = useQuery<CallsResp>({
    queryKey: ["/api/dialer/calls", { direction: "recordings" }],
    queryFn: async () => {
      const res = await fetch("/api/dialer/calls?limit=500", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load recordings");
      return res.json();
    },
    staleTime: 30_000,
  });

  const recordings = (data?.items ?? []).filter((c) => !!c.recordingUrl);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (recordings.length === 0) {
    return <div className="py-10 text-center text-sm text-muted-foreground">No recordings yet.</div>;
  }

  return (
    <div className="space-y-2">
      {recordings.map((call) => {
        const { label: retLabel, isExpiringSoon } = retentionLabel(call.createdAt);
        return (
          <div
            key={call.id}
            className="rounded-lg border border-border/60 p-3 space-y-2"
            data-testid={`row-recording-${call.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Mic className="h-4 w-4 text-primary flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium">
                    {call.direction === "inbound" ? formatPhone(call.fromNumber) : formatPhone(call.toNumber)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {call.createdAt ? format(new Date(call.createdAt), "MMM d, yyyy 'at' h:mm a") : "—"}
                    {(call.durationSeconds ?? 0) > 0 && ` · ${formatDuration(call.durationSeconds)}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 border-border/60 ${isExpiringSoon ? "border-amber-400 text-amber-600 dark:text-amber-400" : ""}`}
                  data-testid={`badge-retention-${call.id}`}
                >
                  {retLabel}
                </Badge>
                <DispositionBadge status={call.status} />
              </div>
            </div>
            <audio controls src={`/api/dialer/calls/${call.id}/recording`} className="w-full h-8" data-testid={`audio-recording-${call.id}`} />
            <div className="flex items-center justify-between">
              <a
                href={`/api/dialer/calls/${call.id}/recording`}
                download
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid={`link-download-recording-${call.id}`}
              >
                <Download className="h-3 w-3" /> Download
              </a>
              {call.notes && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground italic truncate max-w-[200px]">
                  <span className="not-italic font-medium text-foreground/60">Note:</span>
                  {call.notes}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── My Line Card ──────────────────────────────────────────────────────────────
function MyLineCard() {
  const { data: lineData, isLoading } = useQuery<MyLineResp>({
    queryKey: ["/api/dialer/my-line"],
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border/60 p-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading line info…
      </div>
    );
  }

  if (!lineData?.provisioned || !lineData.phoneNumber) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 p-3 text-sm text-muted-foreground text-center">
        No line assigned. Contact your admin.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/60 p-3 flex items-center gap-3" data-testid="card-my-line">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Phone className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold font-mono" data-testid="text-my-line-number">
          {formatPhone(lineData.phoneNumber)}
        </div>
        {lineData.cnam && (
          <div className="text-xs text-muted-foreground">{lineData.cnam}</div>
        )}
        <div className="text-xs text-muted-foreground capitalize">
          {lineData.type || "workspace"} line
        </div>
      </div>
      <Badge className={`border-0 text-[10px] ${lineData.status === "active" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>
        {lineData.status || "active"}
      </Badge>
    </div>
  );
}

// ── Dialer Hub Sheet ──────────────────────────────────────────────────────────
type HubTab = "dialpad" | "activity" | "voicemails" | "recordings";

interface DialerHubProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DialerHub({ open, onOpenChange }: DialerHubProps) {
  const [tab, setTab] = useState<HubTab>("activity");
  // Track whether the hub has been opened (so mark-read fires once on open).
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  const tabs: { id: HubTab; label: string; icon: React.ReactNode }[] = [
    { id: "activity", label: "Activity", icon: <Phone className="h-3.5 w-3.5" /> },
    { id: "dialpad", label: "Dialpad", icon: <Grid3x3 className="h-3.5 w-3.5" /> },
    { id: "voicemails", label: "Voicemails", icon: <Voicemail className="h-3.5 w-3.5" /> },
    { id: "recordings", label: "Recordings", icon: <Mic className="h-3.5 w-3.5" /> },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:w-[460px] p-0 flex flex-col" data-testid="dialer-hub-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/60 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4 text-primary" />
            Dialer Hub
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 py-3 flex-shrink-0">
          <MyLineCard />
        </div>

        {/* Tabs */}
        <div className="px-5 flex-shrink-0">
          <div className="flex gap-1 rounded-lg bg-muted/40 p-0.5">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  tab === t.id
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-hidden px-5 py-3">
          {tab === "dialpad" && (
            <ScrollArea className="h-full">
              <Dialpad active={open} />
            </ScrollArea>
          )}
          {tab === "activity" && <ActivityTab onOpen={open && hasOpened} />}
          {tab === "voicemails" && (
            <ScrollArea className="h-full">
              <VoicemailsTab />
            </ScrollArea>
          )}
          {tab === "recordings" && (
            <ScrollArea className="h-full">
              <RecordingsTab />
            </ScrollArea>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
